import { useFeature } from '@ole-ui/ole-ui-components';
import { Badge } from '@patternfly/react-core';
import ComponentError from 'components/Error/ComponentError';
import UpcomingEvents, {
  Item as UpcomingEventsItem,
} from 'components/UpcomingEvents';
import { EnablementFeatureFlags } from 'config/featureFlags';

import useMobxStores from 'hooks/useMobxStores';
import useUserTimezone from 'hooks/useUserTimezone';
import { observer } from 'mobx-react';
import React from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { TFunction, useTranslation } from 'react-i18next';
import { handleError } from 'services/ErrorService';
import { LiveEvent } from 'types';
import { LandingTab } from 'components/Landing/types';
import { DateTime } from 'luxon';
import useLiveEventsEmptyState from './hooks/useLiveEventsEmptyState';

type EventButtonConfig = {
  label: string;
  analyticsTag: string;
};

const getEventButtonConfig = (
  event: LiveEvent,
  t: TFunction,
): EventButtonConfig => {
  if (event.type === 'exam') {
    return { label: t('View Exam'), analyticsTag: '' };
  }

  if (event.type === 'premvt') {
    return event.isCurrent
      ? { label: t('Attend'), analyticsTag: 'attend-exam' }
      : { label: t('Go to session'), analyticsTag: 'go-to-session' };
  }

  if (event.type === 'ilt' || event.type === 'vt' || event.type === 'lvc') {
    return event.isCurrent
      ? { label: t('Attend'), analyticsTag: 'attend' }
      : { label: t('Go to class'), analyticsTag: 'go-to-class' };
  }

  return { label: t('View'), analyticsTag: '' };
};

const UpcomingLiveEvents = () => {
  const { t } = useTranslation();
  const { routerStore, classesStore } = useMobxStores();
  const { userTimezone } = useUserTimezone();
  const { enrolledLiveEvents, isLoadingLiveEvents } = classesStore;

  const emptyStateConfig = useLiveEventsEmptyState();

  const canView = useFeature({
    section: 'enablements',
    flagName: EnablementFeatureFlags.NewHomePageCanViewUpcomingEvents,
    defaultFlagValue: true,
  });

  const hasUpcomingEvents = !!enrolledLiveEvents?.length;

  const handleEventNavigation = (event: LiveEvent) => {
    if (event.type === 'premvt' && event.id && event.slug) {
      routerStore.navigate('sessions:view', {
        sessionid: event.id,
        sessionslug: event.slug,
      });
    } else if (event.type === 'exam' && event.slug) {
      routerStore.navigate('exams:view', {
        slug: event.slug,
      });
    } else if ((event.type === 'vt' || event.type === 'ilt') && event.id) {
      routerStore.navigate('classes:view', { uuid: event.id });
    } else if (event.type === 'lvc' && event.id) {
      if (!event.isCurrent && event.lmsOfferingSlug) {
        routerStore.navigate('courses:overview', {
          course: event.lmsOfferingSlug,
          tab: LandingTab.LiveClass,
        });
      } else {
        routerStore.navigate('classes:view', { uuid: event.id });
      }
    }
  };

  if (!canView) {
    return null;
  }

  return (
    <section className="my-activity__lane-element my-activity__lane-element--fadein">
      <div className="my-activity__lane-title">
        <h3>{t('My live events')}</h3>
        {hasUpcomingEvents && !isLoadingLiveEvents && (
          <Badge isRead className="my-activity__lane-badge">
            {enrolledLiveEvents?.length || 0}
          </Badge>
        )}
      </div>
      <UpcomingEvents
        className="my-activity__lane-card"
        isLoading={isLoadingLiveEvents}
        emptyState={emptyStateConfig}
      >
        {enrolledLiveEvents?.map((event) => {
          const { label, analyticsTag } = getEventButtonConfig(event, t);

          return (
            <UpcomingEventsItem
              key={event.id}
              id={event.id}
              title={event.title}
              startDate={DateTime.fromISO(event.startTime)}
              endDate={DateTime.fromISO(event.endTime)}
              buttonLabel={label}
              isOngoing={event.isCurrent}
              onView={() => handleEventNavigation(event)}
              timezone={userTimezone}
              dataAnalyticsTag={analyticsTag}
            />
          );
        })}
      </UpcomingEvents>
    </section>
  );
};

export default withErrorBoundary(
  observer(UpcomingLiveEvents),
  ComponentError,
  handleError,
);
